import React, { useEffect, useMemo, useState } from 'react';
import { Filter, Music } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/store';
import {
  getChannelVideos,
  selectVideosState,
  selectAllAudioFeatures
} from './videosSlice';
import { selectAllChannels } from '@/features/channels/channelsSlice';
import { setCurrentVideo, addToQueue } from '@/features/player/playerSlice';
import VideoTable from '@/components/table/VideoTable';
import { analyzeVideo } from '@/services/audio.api';
import { Video } from '@meradio/shared-types';
import { Button } from '@/components/ui';
import { useVideoFilters, VideoFilterBar, ExtraSort } from '@/components/video-filters';
import AddToCratePopover from '@/features/crates/AddToCratePopover';

const Videos: React.FC = () => {
  const [crateTarget, setCrateTarget] = useState<Video | null>(null);
  const dispatch = useAppDispatch();
  const {
    channelVideos,
    isLoading,
    error
  } = useAppSelector(selectVideosState);
  const audioFeatures = useAppSelector(selectAllAudioFeatures);
  const channels = useAppSelector(selectAllChannels);

  const allVideos = useMemo(() => {
    const videosList: Video[] = [];
    Object.values(channelVideos).forEach(channelVideoList => {
      videosList.push(...channelVideoList);
    });
    return videosList;
  }, [channelVideos]);

  const extraSort = useMemo<ExtraSort>(() => ({
    options: [
      { value: 'bpm', label: 'BPM High-Low' },
      { value: 'key', label: 'Musical Key' },
      { value: 'popularity', label: 'Popularity' },
    ],
    compare: (a, b, sortBy) => {
      switch (sortBy) {
        case 'bpm': {
          const bpmA = audioFeatures[a.videoId]?.tempo || 0;
          const bpmB = audioFeatures[b.videoId]?.tempo || 0;
          return bpmB - bpmA;
        }
        case 'key': {
          const keyA = audioFeatures[a.videoId]?.key || 0;
          const keyB = audioFeatures[b.videoId]?.key || 0;
          return keyA - keyB;
        }
        case 'popularity':
          return 0;
        default:
          return null;
      }
    },
  }), [audioFeatures]);

  const {
    filters,
    setFilters,
    resetFilters,
    sortBy,
    setSortBy,
    showFilters,
    setShowFilters,
    filteredVideos,
  } = useVideoFilters(allVideos, audioFeatures, extraSort);

  useEffect(() => {
    channels.forEach(channel => {
      dispatch(getChannelVideos({
        channelId: channel.channelId
      }));
    });
  }, [dispatch, channels]);

  const handlePlayVideo = (video: Video) => {
    dispatch(setCurrentVideo(video));
  };

  const handleAddToQueue = (video: Video) => {
    dispatch(addToQueue(video));
  };

  const handleAnalyze = async (video: Video) => {
    try {
      await analyzeVideo(video.videoId);
    } catch {
      console.error('Failed to queue analysis');
    }
  };

  return (
    <div className="p-0 pt-4 md:p-6 max-w-7xl mx-auto">
      <div className="px-4 md:px-0 flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-meradio-dark dark:text-text-dark-primary">Video Discovery</h1>
        <div className="flex items-center gap-4">
          <div className="text-sm text-neutral-600 dark:text-text-dark-secondary">
            {filteredVideos.length} videos found
          </div>
        </div>
      </div>

      <VideoFilterBar
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        sortBy={sortBy}
        setSortBy={setSortBy}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        channels={channels}
        sortOptions={extraSort.options}
        totalCount={allVideos.length}
      />

      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-meradio-primary mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading videos...</p>
        </div>
      )}

      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-xl p-6 mb-6">
          <p className="text-danger-800">{error}</p>
        </div>
      )}

      {!isLoading && filteredVideos.length > 0 && (
        <VideoTable
          videos={filteredVideos}
          audioFeatures={audioFeatures}
          onPlayVideo={handlePlayVideo}
          onAddToQueue={handleAddToQueue}
          onAnalyze={handleAnalyze}
          onAddToCrate={setCrateTarget}
        />
      )}

      <AddToCratePopover
        videoId={crateTarget?.videoId ?? null}
        videoTitle={crateTarget?.title}
        open={crateTarget !== null}
        onClose={() => setCrateTarget(null)}
      />

      {!isLoading && filteredVideos.length === 0 && allVideos.length === 0 && (
        <div className="text-center py-12">
          <Music className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">No videos found</h3>
          <p className="text-neutral-600 mb-4">
            Start by following some channels to import their videos.
          </p>
        </div>
      )}

      {!isLoading && filteredVideos.length === 0 && allVideos.length > 0 && (
        <div className="text-center py-12">
          <Filter className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">No videos match your filters</h3>
          <p className="text-neutral-600 mb-4">
            Try adjusting your search terms or filter criteria.
          </p>
          <Button variant="link" onClick={resetFilters}>
            Reset filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default Videos;
